import { useState } from 'react';
import Countdown from './Countdown';

const CATEGORY_EMOJI = {
  Clothing: '👕', Shoes: '👟', Beauty: '✨', Electronics: '📱',
  Home: '🏡', Accessories: '👜', Food: '☕', Other: '✦',
};

const truncate = (name, max = 45) => name && name.length > max ? name.slice(0, max) + '...' : name;

function timeElapsed(addedAt) {
  const ms   = Date.now() - addedAt;
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hrs  = Math.floor(ms / (1000 * 60 * 60));
  if (days >= 1) return `${days} day${days !== 1 ? 's' : ''} elapsed`;
  if (hrs  >= 1) return `${hrs} hour${hrs !== 1 ? 's' : ''} elapsed`;
  return 'just now';
}

const JOURNEY_STAGES = ['Queued', 'Simmering', 'Archive', 'Profile'];

function JourneyIndicator({ currentStage }) {
  return (
    <div className="journey-indicator" aria-label="The Simmering Journey">
      <p className="journey-label">The Simmering Journey</p>
      <div className="journey-stages">
        {JOURNEY_STAGES.map((stage, i) => (
          <div key={stage} className="journey-step-wrap">
            <div className={`journey-step ${stage === currentStage ? 'journey-step--active' : i < JOURNEY_STAGES.indexOf(currentStage) ? 'journey-step--done' : ''}`}>
              <span className="journey-step-name">{stage}</span>
            </div>
            {i < JOURNEY_STAGES.length - 1 && (
              <div className={`journey-connector ${i < JOURNEY_STAGES.indexOf(currentStage) ? 'journey-connector--done' : ''}`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ItemCard({ item, onIntent, onDelete, onSkipTimer, isReady }) {
  const [pressed,    setPressed]    = useState(null);
  const [imgError,   setImgError]   = useState(false);
  const [reflection, setReflection] = useState('');

  const handleSkip = () => { setPressed('skip'); onIntent(item.id, 'skipped'); };
  const handleBuy  = () => { setPressed('buy');  onIntent(item.id, 'bought');  };

  const showImage = item.image && !imgError;

  // ── Editorial ready card ──────────────────────────────────────────────────
  if (isReady) {
    return (
      <div className="item-card item-card--ready">

        {/* Hero image */}
        <div className="rc-hero">
          {showImage ? (
            <img
              src={item.image}
              alt={item.name}
              className="rc-hero-image"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="rc-hero-placeholder">
              {CATEGORY_EMOJI[item.category] || '✦'}
            </div>
          )}

          {/* Floating Simmer Complete badge */}
          <div className="rc-badge">
            <span className="material-symbols-outlined rc-badge-icon">check_circle</span>
            Simmer Complete
          </div>

          {/* Delete button */}
          <button
            className="rc-delete-btn"
            onClick={() => onDelete(item.id)}
            aria-label="Remove item"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="rc-body">
          {item.category && (
            <p className="rc-category">
              {CATEGORY_EMOJI[item.category]} {item.category}
            </p>
          )}

          <a href={item.url} target="_blank" rel="noopener noreferrer" className="rc-title-link">
            <h2 className="rc-title">{item.name}</h2>
          </a>

          {item.price && (
            <p className="rc-price">${item.price.toFixed(2)}</p>
          )}

          {/* Reflection card */}
          <div className="rc-reflection-card">
            <div className="rc-reflection-bar" />
            <span className="rc-reflection-quote-icon">❝</span>
            <p className="rc-reflection-label">Reflection</p>
            <textarea
              className="reflection-input"
              placeholder="Why do you want this? How do you feel about it now?"
              value={reflection}
              onChange={e => setReflection(e.target.value)}
              rows={3}
            />
          </div>

          {/* CTA stack */}
          <div className="decision-buttons">
            <button
              className={`btn-buy ${pressed === 'buy' ? 'btn-buy--pressed' : ''}`}
              onClick={handleBuy}
              disabled={pressed !== null}
            >
              Intentional Purchase
            </button>
            <button
              className={`btn-skip ${pressed === 'skip' ? 'btn-skip--pressed' : ''}`}
              onClick={handleSkip}
              disabled={pressed !== null}
            >
              {item.price ? `I don't need this — save $${item.price.toFixed(2)}` : "I don't need this"}
            </button>
          </div>
        </div>

        {/* Journey footer */}
        <div className="rc-footer">
          <JourneyIndicator currentStage="Simmering" />

          <div className="rc-footer-grid">
            <div className="rc-elapsed">
              <div className="rc-elapsed-icon">
                <span className="material-symbols-outlined">timer</span>
              </div>
              <div>
                <p className="rc-elapsed-time">{timeElapsed(item.addedAt)}</p>
                <p className="rc-elapsed-label">Reflection period complete.</p>
              </div>
            </div>

            <div className="rc-verified">
              <div>
                <p className="rc-verified-title">Intentional Purchase</p>
                <p className="rc-verified-sub">Verified by Simmer</p>
              </div>
              <span className="material-symbols-outlined rc-verified-icon">verified</span>
            </div>
          </div>
        </div>

      </div>
    );
  }

  // ── Compact waiting card ──────────────────────────────────────────────────
  return (
    <div className="item-card">
      <div className="item-card-inner">
        <div className="item-main">

          {showImage && (
            <img
              src={item.image}
              alt={item.name}
              className={`item-thumb${item.image.includes('logo.clearbit.com') ? ' item-thumb--logo' : ''}`}
              onError={() => setImgError(true)}
            />
          )}

          <div className="item-info">
            <a href={item.url} target="_blank" rel="noopener noreferrer" className="item-name">
              {truncate(item.name)}
            </a>

            <div className="item-meta">
              {item.price && (
                <span className="item-price">${item.price.toFixed(2)}</span>
              )}
              {item.category && item.category !== 'Other' && (
                <span className="item-category">
                  {CATEGORY_EMOJI[item.category]} {item.category}
                </span>
              )}
            </div>

            {item.siteName && (
              <span className="item-site">{item.siteName}</span>
            )}

            <span className="item-elapsed">{timeElapsed(item.addedAt)}</span>
          </div>

          <div className="item-actions">
            <button
              className="skip-timer-btn"
              onClick={() => onSkipTimer(item.id)}
              title="Skip the wait"
              aria-label="Skip wait and decide now"
            >
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                <path d="M1.5 2.5L5.5 6.5L1.5 10.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6.5 2.5L10.5 6.5L6.5 10.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button className="delete-btn" onClick={() => onDelete(item.id)} aria-label="Remove item">
              ×
            </button>
          </div>
        </div>

        <Countdown addedAt={item.addedAt} expiresAt={item.expiresAt} />
      </div>
    </div>
  );
}
